import { Link } from "react-router-dom";
import { ArrowRight, Heart, Leaf, Globe, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";

export const AboutUs = () => {
  return (
    <div className="min-h-screen bg-navy-900 pt-20">
      <SEO 
        title="About Us | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Founded by Cyril Davroux, Magic C Plumbing blends French elegance, international experience, and raw materials to create timeless spaces where nature meets refined living."
        canonical="/about"
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Where Art Meets <span className="text-sage-400">Life</span>
            </h1>
            <p className="text-2xl text-gray-300 mb-8 italic">
              "Art is Life. Life is Art."
            </p>
            <p className="text-xl text-gray-300 leading-relaxed">
              At Magic C Plumbing, we believe that true luxury emerges where raw nature, refined art, and human ingenuity converge in perfect harmony.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Story</h2>
            </div>
            
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Founded by <span className="text-sage-400 font-semibold">Cyril Davroux</span>, a visionary engineer and passionate connoisseur of art, technology, and design, Magic C Plumbing was born from a lifelong journey across the world. Deeply moved by the silent power of nature and the poetry of raw materials, Cyril approaches every project with the conviction that "Art is Life. Life is Art."
              </p>
              
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                Our international team is composed of passionate individuals — designers, engineers, and master craftsmen from different cultures — united by a shared obsession with beauty, quality, and authenticity. We blend the best talents and the most advanced technologies to create spaces that touch the soul and elevate everyday life.
              </p>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Inspired by years of travel, we celebrate the raw beauty of noble materials: unpolished stone, warm wood, textured concrete, aged brass, and volcanic rock. We are passionate about materials in their most honest form — those that carry the memory of the earth and reveal their true character with time. For us, a material is not just a surface; it is a living element that tells a story.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* International Presence */}
      <section className="py-20 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Global Presence
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From wild serenity to elegant sophistication, we operate from three strategic locations
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
              <CardContent className="p-8 text-center">
                <Globe className="h-12 w-12 text-sage-400 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-white mb-3">Wimbledon Office</h3>
                <p className="text-gray-300 mb-4">International Projects & Operations</p>
                <p className="text-sm text-sage-400">Office: 0333 789 0987</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
              <CardContent className="p-8 text-center">
                <Award className="h-12 w-12 text-sage-400 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-white mb-3">Paris Office</h3>
                <p className="text-gray-300 mb-4">Art & Design Reference</p>
                <p className="text-sm text-sage-400">French Elegance & Refinement</p>
              </CardContent>
            </Card>

            <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
              <CardContent className="p-8 text-center">
                <Leaf className="h-12 w-12 text-sage-400 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-white mb-3">Bali Office</h3>
                <p className="text-gray-300 mb-4">Sourcing & Materials</p>
                <p className="text-sm text-sage-400">Raw Materials & Natural Elements</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Philosophy</h2>
            </div>
            
            <div className="prose prose-lg prose-invert max-w-none mb-12">
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                From the wild serenity of tropical landscapes to the elegant sophistication of London residences, we craft sanctuaries where raw nature meets French artistic refinement. Every detail is handcrafted with care, every line drawn with emotion, and every technical solution designed with respect.
              </p>
              
              <p className="text-xl text-gray-300 leading-relaxed mb-12">
                We anticipate the challenges of tomorrow: the rising cost of energy, the sacred value of water, and the urgent need to protect our planet. That is why we design intelligent, energy-efficient, and environmentally responsible solutions that combine exceptional comfort with deep ecological awareness.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-sage-600/10 backdrop-blur-md border-sage-600/30">
                <CardContent className="p-8">
                  <Heart className="h-10 w-10 text-sage-400 mb-4" />
                  <h3 className="text-2xl font-semibold text-white mb-4">Our Values</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-sage-400 mr-2">•</span>
                      <span><strong className="text-white">Water is life.</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-400 mr-2">•</span>
                      <span><strong className="text-white">Energy is harmony.</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-400 mr-2">•</span>
                      <span><strong className="text-white">Materials are memory.</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-sage-400 mr-2">•</span>
                      <span><strong className="text-white">Design is respect.</strong></span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <p className="text-xl italic text-gray-300 mb-2">
                      "Beauty is the promise of happiness."
                    </p>
                    <p className="text-sm text-sage-400">— Stendhal</p>
                  </div>
                  <div>
                    <p className="text-xl italic text-gray-300 mb-2">
                      "The details are not the details. They make the design."
                    </p>
                    <p className="text-sm text-sage-400">— Charles Eames</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-2xl text-gray-300 leading-relaxed mb-8">
              At Magic C Plumbing, French elegance, international experience, and a profound passion for raw materials come together to create timeless spaces — where technology disappears into beauty, where nature is honored, and where every project becomes a living work of art.
            </p>
            
            <div className="bg-white/5 backdrop-blur-md border-white/10 rounded-xl p-10 mb-10">
              <p className="text-xl text-white mb-4">
                We are more than creators of plumbing and heating systems.
              </p>
              <p className="text-2xl font-semibold text-sage-400 mb-4">
                We are curators of emotion, guardians of authenticity, and architects of spaces where raw beauty and refined living coexist in perfect balance.
              </p>
            </div>

            <p className="text-3xl font-bold text-white mb-12">
              Welcome to Magic C Plumbing
            </p>
            
            <p className="text-xl text-gray-300 mb-10 italic">
              Where passionate international talents, advanced technology, and the raw poetry of materials unite to transform visions into timeless works of art.
            </p>

            <Link to="/contact">
              <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white text-xl px-12 py-8">
                Begin Your Journey
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
